import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  formatDate,
  formatMonthYear,
  formatNumber,
  formatWeekdayShort,
} from '@/lib/i18n/format'

import { ActivityCard } from './activity-card'
import { useAppLocale } from '../hooks/ui'
import type { Activity } from '../types'

function dateKey(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function activityDateKey(activity: Activity): string | null {
  if (!activity.startAt) return null
  const date = new Date(activity.startAt)
  return Number.isNaN(date.getTime()) ? null : dateKey(date)
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1)
}

/**
 * Agenda dots describe the viewer's relationship, not the category: teal for
 * joined/created, Sole for pending and Lilla for the waitlist. Coral stays
 * reserved for the primary action.
 */
function statusColors(activities: Activity[]): string[] {
  const colors: string[] = []
  if (
    activities.some((activity) =>
      ['creator', 'joined', 'attended'].includes(activity.viewerStatus ?? ''),
    )
  ) {
    colors.push('var(--teal)')
  }
  if (activities.some((activity) => activity.viewerStatus === 'pending')) {
    colors.push('var(--sole)')
  }
  if (activities.some((activity) => activity.viewerStatus === 'waitlisted')) {
    colors.push('var(--lilla)')
  }
  return colors.slice(0, 3)
}

interface AgendaCalendarProps {
  activities: Activity[]
}

/**
 * Month grid with up to three status dots per day. The selected day lists its
 * activities as real cards, with a pending/waitlist pill above the card, as in
 * the Flutter agenda.
 */
export function AgendaCalendar({ activities }: AgendaCalendarProps) {
  const { t } = useTranslation()
  const locale = useAppLocale()
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const [selected, setSelected] = useState(() => new Date())

  const byDate = useMemo(() => {
    const map = new Map<string, Activity[]>()
    for (const activity of activities) {
      const key = activityDateKey(activity)
      if (!key) continue
      const current = map.get(key) ?? []
      current.push(activity)
      map.set(key, current)
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const left = a.startAt ? Date.parse(a.startAt) : 0
        const right = b.startAt ? Date.parse(b.startAt) : 0
        return left - right
      })
    }
    return map
  }, [activities])

  const monthCount = useMemo(
    () =>
      activities.filter((activity) => {
        const key = activityDateKey(activity)
        if (!key) return false
        return key.startsWith(
          `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`,
        )
      }).length,
    [activities, month],
  )

  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1)
    const offset = (first.getDay() + 6) % 7
    const daysInMonth = new Date(
      month.getFullYear(),
      month.getMonth() + 1,
      0,
    ).getDate()
    return [
      ...Array.from({ length: offset }, () => null),
      ...Array.from(
        { length: daysInMonth },
        (_, index) =>
          new Date(month.getFullYear(), month.getMonth(), index + 1),
      ),
    ]
  }, [month])

  const weekdays = useMemo(() => {
    // 2024-01-01 was a Monday; reuse it for localized weekday abbreviations.
    return Array.from({ length: 7 }, (_, index) =>
      formatWeekdayShort(new Date(2024, 0, 1 + index), locale),
    )
  }, [locale])

  const selectedKey = dateKey(selected)
  const selectedActivities = byDate.get(selectedKey) ?? []
  const todayKey = dateKey(new Date())

  function selectMonth(next: Date) {
    setMonth(next)
    const now = new Date()
    setSelected(
      now.getFullYear() === next.getFullYear() && now.getMonth() === next.getMonth()
        ? now
        : new Date(next.getFullYear(), next.getMonth(), 1),
    )
  }

  return (
    <div className="rounded-card border border-line bg-white p-3 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="grid size-11 place-items-center rounded-full hover:bg-mint"
          onClick={() => selectMonth(addMonths(month, -1))}
          aria-label={t('discovery.agenda.previousMonth')}
        >
          <CaretLeft size={20} weight="bold" aria-hidden="true" />
        </button>
        <h2 className="text-xl" aria-live="polite">
          {formatMonthYear(month, locale)}
        </h2>
        <button
          type="button"
          className="grid size-11 place-items-center rounded-full hover:bg-mint"
          onClick={() => selectMonth(addMonths(month, 1))}
          aria-label={t('discovery.agenda.nextMonth')}
        >
          <CaretRight size={20} weight="bold" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1 text-center">
        {weekdays.map((weekday) => (
          <span key={weekday} className="py-1 text-xs font-bold text-muted">
            {weekday.slice(0, 2)}
          </span>
        ))}
        {cells.map((day, index) => {
          if (!day) return <span key={`blank-${index}`} aria-hidden="true" />
          const key = dateKey(day)
          const activitiesForDay = byDate.get(key) ?? []
          const isSelected = key === selectedKey
          const isToday = key === todayKey
          const markerColors = statusColors(activitiesForDay)
          return (
            <button
              key={key}
              type="button"
              className={`agenda-day ${isSelected ? 'is-selected' : ''}`}
              aria-pressed={isSelected}
              aria-current={isToday ? 'date' : undefined}
              aria-label={formatDate(day, locale, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
              onClick={() => setSelected(day)}
            >
              <span className={isToday ? 'font-bold text-teal' : undefined}>
                {day.getDate()}
              </span>
              <span className="mt-1 flex h-2 items-center justify-center gap-0.5">
                {markerColors.map((color) => (
                  <span
                    key={color}
                    className="size-1.5 rounded-full"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                ))}
              </span>
            </button>
          )
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
        <Legend color="var(--teal)" labelKey="discovery.agenda.legendJoined" />
        <Legend color="var(--sole)" labelKey="discovery.agenda.legendPending" />
        <Legend
          color="var(--lilla)"
          labelKey="discovery.agenda.legendWaitlist"
        />
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-muted">
          <span
            className="size-2.5 rounded-[3px] border-[1.5px] border-teal"
            aria-hidden="true"
          />
          {t('discovery.agenda.legendToday')}
        </span>
      </div>

      <p className="mt-3 text-sm font-semibold text-muted" role="status">
        {t('discovery.agenda.monthCount', {
          count: monthCount,
          formatted: formatNumber(monthCount, locale),
        })}
      </p>

      <h3 className="mt-4 text-lg">
        {formatDate(selected, locale, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
      </h3>

      {selectedActivities.length === 0 ? (
        <p className="mt-2 text-sm text-muted">
          {t('discovery.agenda.dayEmpty')}
        </p>
      ) : (
        <div className="mt-2 space-y-3">
          {selectedActivities.map((activity) => (
            <div key={activity.id}>
              {activity.viewerStatus === 'pending' ? (
                <span className="mb-1.5 inline-flex rounded-full bg-[var(--sole-tint)] px-2.5 py-1 text-xs font-bold text-deep">
                  {t('discovery.agenda.pending')}
                </span>
              ) : null}
              {activity.viewerStatus === 'waitlisted' ? (
                <span className="mb-1.5 inline-flex rounded-full bg-[var(--lilla-tint)] px-2.5 py-1 text-xs font-bold text-deep">
                  {activity.waitlistPosition
                    ? t('discovery.agenda.waitlistPosition', {
                        position: activity.waitlistPosition,
                      })
                    : t('discovery.agenda.waitlist')}
                </span>
              ) : null}
              <ActivityCard activity={activity} showSave={false} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Legend({ color, labelKey }: { color: string; labelKey: string }) {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-muted">
      <span
        className="size-2.5 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      {t(labelKey)}
    </span>
  )
}
